import React, { useEffect, useState } from 'react';
import {
    Form, Input, Switch, Button, Table, Modal, notification,
    Popconfirm, Tag, Space, Row, Col, InputNumber, Select, Typography,
    Upload, Image,
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, SaveOutlined, ReloadOutlined,
    UploadOutlined, LoadingOutlined, GlobalOutlined, ShopOutlined,
    PictureOutlined, CheckCircleFilled, CloseCircleFilled, ThunderboltOutlined,
} from '@ant-design/icons';
import imageCompression from 'browser-image-compression';
import AdminLayout from '../../components/admin/AdminLayout';
import adminApi from '../../api/adminAxios';

const { Text } = Typography;
const { Option } = Select;

const tiers = ['Platinum', 'Gold', 'Silver', 'Bronze', 'Basic'];
const tierColor = { Platinum: '#6366F1', Gold: '#F59E0B', Silver: '#64748B', Bronze: '#B45309', Basic: '#10B981' };

const ImageUploadField = ({ value, onChange, label }) => {
    const [uploading, setUploading] = useState(false);

    const handleUpload = async (file) => {
        setUploading(true);
        try {
            const compressed = await imageCompression(file, { maxSizeMB: 1, useWebWorker: true });
            const formData = new FormData();
            formData.append('file', new File([compressed], file.name, { type: compressed.type }));
            const res = await adminApi.post('/admin/settings/brand-partnership/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const path = res.data.data.path;
            onChange(`${process.env.REACT_APP_IMAGE_URL}/${path}`);
            notification.success({ message: 'Image Uploaded', description: `${label || 'Image'} has been uploaded successfully.` });
        } catch (_) {
            notification.error({ message: 'Upload Failed', description: 'Could not upload the image. Please try again.' });
        } finally {
            setUploading(false);
        }
        return false;
    };

    return (
        <div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Input
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="https://... or upload below"
                    style={{ flex: 1 }}
                />
                <Upload accept="image/jpeg,image/png,image/jpg" showUploadList={false} beforeUpload={handleUpload}>
                    <Button
                        icon={uploading ? <LoadingOutlined /> : <UploadOutlined />}
                        disabled={uploading}
                    >
                        {uploading ? 'Uploading...' : 'Upload'}
                    </Button>
                </Upload>
            </div>
            {value && (
                <div style={{
                    marginTop: 10, padding: 10, background: 'var(--color-bg-subtle, #F8FAFC)',
                    border: '1px solid var(--color-border)', borderRadius: 8,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    minHeight: 64,
                }}>
                    <img
                        src={value}
                        alt=""
                        style={{ maxHeight: 80, maxWidth: '100%', objectFit: 'contain' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                </div>
            )}
        </div>
    );
};

export default function AdminBrandPartnership() {
    const [settings, setSettings] = useState(null);
    const [adSpots, setAdSpots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [settingsSaving, setSettingsSaving] = useState(false);
    const [spotModal, setSpotModal] = useState({ open: false, editing: null });
    const [spotSaving, setSpotSaving] = useState(false);

    const [settingsForm] = Form.useForm();
    const [spotForm] = Form.useForm();

    const fetchData = () => {
        setLoading(true);
        adminApi.get('/admin/settings/brand-partnership')
            .then((res) => {
                const d = res.data.data;
                setSettings(d.setting);
                setAdSpots(d.ad_spots || []);
                if (d.setting) {
                    settingsForm.setFieldsValue({
                        enabled: d.setting.enabled,
                        official_partner_name: d.setting.official_partner_name,
                        official_partner_logo_url: d.setting.official_partner_logo_url,
                        official_partner_website_url: d.setting.official_partner_website_url,
                        official_partner_label: d.setting.official_partner_label,
                        auth_background_image_url: d.setting.auth_background_image_url,
                        email_sponsor_banner_url: d.setting.email_sponsor_banner_url,
                    });
                }
            })
            .catch(() => notification.error({ message: 'Failed to Load', description: 'Could not fetch brand partnership settings. Please try again.' }))
            .finally(() => setLoading(false));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchData(); }, []);

    const saveSettings = async (values) => {
        setSettingsSaving(true);
        try {
            await adminApi.post('/admin/settings/brand-partnership/update', values);
            notification.success({ message: 'Settings Saved', description: 'Brand partnership settings have been updated successfully.' });
            fetchData();
        } catch (err) {
            const errors = err.response?.data?.errors;
            if (errors) {
                Object.values(errors).flat().forEach((m) => notification.error({ message: 'Validation Error', description: m }));
            } else {
                notification.error({ message: 'Failed to Save', description: 'Could not save the settings. Please try again.' });
            }
        } finally {
            setSettingsSaving(false);
        }
    };

    const openCreateSpot = () => {
        spotForm.resetFields();
        spotForm.setFieldsValue({ active: true, sort_order: adSpots.length, duration: 30 });
        setSpotModal({ open: true, editing: null });
    };

    const openEditSpot = (spot) => {
        spotForm.setFieldsValue(spot);
        setSpotModal({ open: true, editing: spot });
    };

    const saveSpot = async (values) => {
        setSpotSaving(true);
        try {
            if (spotModal.editing) {
                await adminApi.put(`/admin/settings/brand-partnership/ad-spots/${spotModal.editing.id}`, values);
                notification.success({ message: 'Ad Spot Updated', description: 'The advertisement spot has been updated successfully.' });
            } else {
                await adminApi.post('/admin/settings/brand-partnership/ad-spots', values);
                notification.success({ message: 'Ad Spot Created', description: 'The new advertisement spot has been created successfully.' });
            }
            setSpotModal({ open: false, editing: null });
            fetchData();
        } catch (err) {
            const errors = err.response?.data?.errors;
            if (errors) {
                Object.values(errors).flat().forEach((m) => notification.error({ message: 'Validation Error', description: m }));
            } else {
                notification.error({ message: 'Failed to Save', description: 'Could not save the ad spot. Please try again.' });
            }
        } finally {
            setSpotSaving(false);
        }
    };

    const deleteSpot = async (id) => {
        try {
            await adminApi.delete(`/admin/settings/brand-partnership/ad-spots/${id}`);
            notification.success({ message: 'Ad Spot Deleted', description: 'The advertisement spot has been removed successfully.' });
            fetchData();
        } catch (_) {
            notification.error({ message: 'Failed to Delete', description: 'Could not delete the ad spot. Please try again.' });
        }
    };

    const activeSpots = adSpots.filter((s) => s.active).length;

    const spotColumns = [
        {
            title: 'Tier',
            dataIndex: 'tier',
            width: 120,
            render: (v) => (
                <Tag style={{
                    background: `${tierColor[v]}18`,
                    color: tierColor[v] || '#64748B',
                    border: `1px solid ${tierColor[v] || '#ccc'}40`,
                    fontWeight: 700,
                    fontSize: 12,
                }}>
                    {v}
                </Tag>
            ),
        },
        {
            title: 'Ad Image',
            dataIndex: 'image_url',
            width: 100,
            render: (v) => v ? (
                <Image
                    src={v}
                    alt="Ad"
                    style={{ height: 44, width: 80, objectFit: 'contain', borderRadius: 4, background: '#f8fafc', border: '1px solid #e2e8f0' }}
                />
            ) : <Text style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>No image</Text>,
        },
        {
            title: 'Brand',
            dataIndex: 'brand_name',
            render: (v, row) => (
                <div>
                    <div style={{ fontWeight: 600, color: 'var(--color-text-primary)', fontSize: 13 }}>{v || '—'}</div>
                    {row.brand_website_url && (
                        <a href={row.brand_website_url} target="_blank" rel="noreferrer"
                            style={{ fontSize: 11, color: 'var(--color-primary)', display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
                            <GlobalOutlined style={{ fontSize: 10 }} /> Website
                        </a>
                    )}
                </div>
            ),
        },
        {
            title: 'Duration',
            dataIndex: 'duration',
            width: 100,
            render: (v) => <Text style={{ fontSize: 13 }}>{v}s</Text>,
        },
        {
            title: 'Sort',
            dataIndex: 'sort_order',
            width: 70,
            render: (v) => <Text style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>#{v}</Text>,
        },
        {
            title: 'Status',
            dataIndex: 'active',
            width: 100,
            render: (v) => v
                ? <Tag color="green" style={{ fontWeight: 600 }}>Active</Tag>
                : <Tag color="default" style={{ fontWeight: 600 }}>Inactive</Tag>,
        },
        {
            title: 'Actions',
            width: 90,
            render: (_, row) => (
                <Space>
                    <Button type="text" size="small" icon={<EditOutlined />}
                        onClick={() => openEditSpot(row)}
                        style={{ color: 'var(--color-primary)' }}
                    />
                    <Popconfirm title="Delete this ad spot?" onConfirm={() => deleteSpot(row.id)} okButtonProps={{ danger: true }}>
                        <Button type="text" size="small" icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <AdminLayout title="Brand Partnership">
            <div className="admin-page-header">
                <h1 className="admin-page-title">Brand Partnership</h1>
                <p className="admin-page-subtitle">Manage official partner settings and advertisement spots</p>
            </div>

            <Form form={settingsForm} layout="vertical" onFinish={saveSettings}>

                {/* ── Status banner ── */}
                <div className="admin-card" style={{ marginBottom: 20 }}>
                    <div className="admin-card-body">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                <div style={{
                                    width: 48, height: 48, borderRadius: 12, flexShrink: 0,
                                    background: settings?.enabled ? 'rgba(173,20,87,0.1)' : '#F1F5F9',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 20, color: settings?.enabled ? 'var(--color-primary)' : '#94A3B8',
                                }}>
                                    <ShopOutlined />
                                </div>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--color-text-primary)', lineHeight: 1.3 }}>
                                        Brand Partnership Program
                                    </div>
                                    <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 2 }}>
                                        {settings?.official_partner_name
                                            ? `Partner: ${settings.official_partner_name}`
                                            : 'No official partner configured'}
                                        {' · '}
                                        {activeSpots} active ad spot{activeSpots !== 1 ? 's' : ''} of {adSpots.length}
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                                {settings?.enabled
                                    ? <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, color: '#10B981', fontSize: 13 }}>
                                        <CheckCircleFilled /> Enabled
                                    </span>
                                    : <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, color: '#94A3B8', fontSize: 13 }}>
                                        <CloseCircleFilled /> Disabled
                                    </span>
                                }
                                <Form.Item name="enabled" valuePropName="checked" style={{ margin: 0 }}>
                                    <Switch />
                                </Form.Item>
                                <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>Refresh</Button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Settings cards ── */}
                <Row gutter={[20, 20]} style={{ marginBottom: 20 }}>

                    {/* Official Partner */}
                    <Col xs={24} lg={12}>
                        <div className="admin-card" style={{ height: '100%' }}>
                            <div className="admin-card-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <ShopOutlined style={{ color: 'var(--color-primary)', fontSize: 15 }} />
                                    <h3 className="admin-card-title" style={{ margin: 0 }}>Official Partner</h3>
                                </div>
                            </div>
                            <div className="admin-card-body">
                                <Row gutter={12}>
                                    <Col span={14}>
                                        <Form.Item name="official_partner_name" label="Partner Name" style={{ marginBottom: 14 }}>
                                            <Input placeholder="e.g. Example Jewellers" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={10}>
                                        <Form.Item name="official_partner_label" label="Display Label" style={{ marginBottom: 14 }}>
                                            <Input placeholder="Official Partner" />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item name="official_partner_logo_url" label="Partner Logo" style={{ marginBottom: 14 }}>
                                    <ImageUploadField label="Partner Logo" />
                                </Form.Item>

                                <Form.Item name="official_partner_website_url" label="Partner Website URL" style={{ marginBottom: 0 }}>
                                    <Input
                                        prefix={<GlobalOutlined style={{ color: 'var(--color-text-muted)' }} />}
                                        placeholder="https://example.com"
                                    />
                                </Form.Item>
                            </div>
                        </div>
                    </Col>

                    {/* Email Sponsor Banner + Auth Background stacked on right */}
                    <Col xs={24} lg={12}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, height: '100%' }}>

                            <div className="admin-card">
                                <div className="admin-card-header">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <PictureOutlined style={{ color: 'var(--color-primary)', fontSize: 15 }} />
                                        <h3 className="admin-card-title" style={{ margin: 0 }}>Email Sponsor Banner</h3>
                                    </div>
                                </div>
                                <div className="admin-card-body">
                                    <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 14, marginTop: 0 }}>
                                        This banner appears at the bottom of all outgoing emails when Brand Partnership is enabled.
                                    </p>
                                    <Form.Item name="email_sponsor_banner_url" label="Banner Image" style={{ marginBottom: 0 }}>
                                        <ImageUploadField label="Email Sponsor Banner" />
                                    </Form.Item>
                                </div>
                            </div>

                            <div className="admin-card">
                                <div className="admin-card-header">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <PictureOutlined style={{ color: 'var(--color-primary)', fontSize: 15 }} />
                                        <h3 className="admin-card-title" style={{ margin: 0 }}>Auth Page Background</h3>
                                    </div>
                                </div>
                                <div className="admin-card-body">
                                    <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 14, marginTop: 0 }}>
                                        This image appears as the background on the login and registration pages when Brand Partnership is enabled.
                                    </p>
                                    <Form.Item name="auth_background_image_url" label="Background Image" style={{ marginBottom: 0 }}>
                                        <ImageUploadField label="Auth Background" />
                                    </Form.Item>
                                </div>
                            </div>

                        </div>
                    </Col>
                </Row>

                {/* Save button */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
                    <Button
                        type="primary"
                        htmlType="submit"
                        icon={<SaveOutlined />}
                        loading={settingsSaving}
                        size="large"
                        style={{ background: 'var(--gradient-primary)', border: 'none', minWidth: 160 }}
                    >
                        Save Settings
                    </Button>
                </div>
            </Form>

            {/* ── Ad Spots ── */}
            <div className="admin-card">
                <div className="admin-card-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <ThunderboltOutlined style={{ color: 'var(--color-primary)', fontSize: 15 }} />
                        <h3 className="admin-card-title" style={{ margin: 0 }}>
                            Ad Spots
                            <Text style={{ fontSize: 13, fontWeight: 400, color: 'var(--color-text-muted)', marginLeft: 8 }}>
                                {activeSpots} active · {adSpots.length} total
                            </Text>
                        </h3>
                    </div>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={openCreateSpot}
                        style={{ background: 'var(--gradient-primary)', border: 'none' }}
                    >
                        Add Spot
                    </Button>
                </div>
                <Table
                    className="admin-table"
                    dataSource={adSpots}
                    columns={spotColumns}
                    rowKey="id"
                    loading={{ spinning: loading, indicator: <LoadingOutlined style={{ fontSize: 32, color: '#AD1457' }} spin /> }}
                    pagination={false}
                    scroll={{ x: 600 }}
                    size="middle"
                />
            </div>

            {/* Ad Spot Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <ThunderboltOutlined style={{ color: 'var(--color-primary)' }} />
                        {spotModal.editing ? 'Edit Ad Spot' : 'New Ad Spot'}
                    </div>
                }
                open={spotModal.open}
                onCancel={() => setSpotModal({ open: false, editing: null })}
                onOk={() => spotForm.submit()}
                okText={spotModal.editing ? 'Save Changes' : 'Create'}
                confirmLoading={spotSaving}
                width={Math.min(520, window.innerWidth - 32)}
                centered
                styles={{ body: { maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden', paddingRight: 8 } }}
            >
                <Form form={spotForm} layout="vertical" onFinish={saveSpot}>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item name="tier" label="Tier" rules={[{ required: true, message: 'Tier is required' }]}>
                                <Select placeholder="Select tier">
                                    {tiers.map((t) => (
                                        <Option key={t} value={t}>{t}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="duration" label="Duration (seconds)" rules={[{ required: true, message: 'Required' }]}>
                                <InputNumber min={1} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={12}>
                        <Col span={14}>
                            <Form.Item name="brand_name" label="Brand Name">
                                <Input placeholder="e.g. Example Brand" />
                            </Form.Item>
                        </Col>
                        <Col span={10}>
                            <Form.Item name="sort_order" label="Sort Order">
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="brand_website_url" label="Brand Website URL">
                        <Input
                            prefix={<GlobalOutlined style={{ color: 'var(--color-text-muted)' }} />}
                            placeholder="https://example.com"
                        />
                    </Form.Item>

                    <Form.Item name="image_url" label="Ad Image">
                        <ImageUploadField label="Ad Image" />
                    </Form.Item>

                    <Form.Item name="active" label="Active" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </AdminLayout>
    );
}
